import React, { useState, useEffect } from 'react';
import { useInstallPrompt, usePushNotifications, useOnlineStatus } from '../hooks/usePWA';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { X, Download, Bell, Smartphone, Wifi, WifiOff, Share } from 'lucide-react';

// Install App Banner Component
export const InstallBanner = () => {
  const { canInstall, isInstalled, isIOS, promptInstall } = useInstallPrompt();
  const { language } = useLanguage();
  const [dismissed, setDismissed] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    const wasDismissed = localStorage.getItem('pwa-install-dismissed');
    if (wasDismissed) {
      const dismissedDate = new Date(wasDismissed);
      const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      // Show again after 7 days
      if (daysSinceDismissed < 7) {
        setDismissed(true);
      }
    }
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('pwa-install-dismissed', new Date().toISOString());
  };

  const handleInstall = async () => {
    const installed = await promptInstall();
    if (installed) {
      setDismissed(true);
    }
  };

  // Don't show if already installed or dismissed
  if (isInstalled || dismissed) return null;

  // Show iOS-specific instructions
  if (isIOS) {
    return (
      <>
        <div className="fixed bottom-20 left-4 right-4 z-50 animate-in slide-in-from-bottom md:left-auto md:right-4 md:w-96">
          <Card className="border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 shadow-lg">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center shrink-0">
                  <Smartphone className="h-5 w-5 text-orange-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900 text-sm">
                    {language === 'fr' ? 'Installer HandyVerse Pro' : 'Install HandyVerse Pro'}
                  </h3>
                  <p className="text-xs text-slate-600 mt-1">
                    {language === 'fr' 
                      ? 'Accédez rapidement à votre tableau de bord'
                      : 'Quick access to your dashboard'
                    }
                  </p>
                  <Button
                    size="sm"
                    onClick={() => setShowIOSGuide(true)}
                    className="mt-2 bg-orange-600 hover:bg-orange-700 text-white rounded-full text-xs h-8"
                  >
                    <Share className="h-3 w-3 mr-1" />
                    {language === 'fr' ? 'Comment installer' : 'How to install'}
                  </Button>
                </div>
                <button
                  onClick={handleDismiss}
                  className="text-slate-400 hover:text-slate-600 p-1"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* iOS Installation Guide Modal */}
        {showIOSGuide && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center p-4">
            <Card className="w-full max-w-md animate-in slide-in-from-bottom">
              <CardContent className="pt-6">
                <h3 className="font-semibold text-lg text-slate-900 mb-4">
                  {language === 'fr' ? 'Installer sur iPhone/iPad' : 'Install on iPhone/iPad'}
                </h3>
                <ol className="space-y-3 text-sm text-slate-600">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center shrink-0 text-xs font-bold">1</span>
                    <span>{language === 'fr' ? 'Appuyez sur le bouton Partager' : 'Tap the Share button'} <Share className="inline h-4 w-4" /></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center shrink-0 text-xs font-bold">2</span>
                    <span>{language === 'fr' ? 'Faites défiler et appuyez sur "Sur l\'écran d\'accueil"' : 'Scroll and tap "Add to Home Screen"'}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center shrink-0 text-xs font-bold">3</span>
                    <span>{language === 'fr' ? 'Appuyez sur "Ajouter"' : 'Tap "Add"'}</span>
                  </li>
                </ol>
                <Button
                  onClick={() => setShowIOSGuide(false)}
                  className="w-full mt-6 bg-slate-900 hover:bg-slate-800 rounded-full"
                >
                  {language === 'fr' ? 'Compris !' : 'Got it!'}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </>
    );
  }

  // Standard install prompt for Android/Desktop
  if (!canInstall) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 z-50 animate-in slide-in-from-bottom md:left-auto md:right-4 md:w-96">
      <Card className="border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 shadow-lg">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center shrink-0">
              <Download className="h-5 w-5 text-orange-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-slate-900 text-sm">
                {language === 'fr' ? 'Installer HandyVerse Pro' : 'Install HandyVerse Pro'}
              </h3>
              <p className="text-xs text-slate-600 mt-1">
                {language === 'fr' 
                  ? 'Accès rapide depuis votre écran d\'accueil'
                  : 'Quick access from your home screen'
                }
              </p>
              <Button
                size="sm"
                onClick={handleInstall}
                className="mt-2 bg-orange-600 hover:bg-orange-700 text-white rounded-full text-xs h-8"
              >
                <Download className="h-3 w-3 mr-1" />
                {language === 'fr' ? 'Installer' : 'Install'}
              </Button>
            </div>
            <button
              onClick={handleDismiss}
              className="text-slate-400 hover:text-slate-600 p-1"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Notification Permission Banner
export const NotificationBanner = () => {
  const { permission, requestPermission, testNotification, isSupported, loading } = usePushNotifications();
  const { language } = useLanguage();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const wasDismissed = localStorage.getItem('notification-banner-dismissed');
    if (wasDismissed) setDismissed(true);
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('notification-banner-dismissed', 'true');
  };

  const handleEnable = async () => {
    const success = await requestPermission();
    if (success) {
      handleDismiss();
      // Send a test notification
      setTimeout(() => testNotification(), 1000);
    }
  };

  if (!isSupported || permission !== 'default' || dismissed) return null;

  return (
    <div className="bg-blue-50 border-b border-blue-100 px-4 py-3">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Bell className="h-5 w-5 text-blue-600" />
          <p className="text-sm text-blue-800">
            {language === 'fr' 
              ? 'Activez les notifications pour ne manquer aucune demande'
              : 'Enable notifications to never miss a request'
            }
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={handleDismiss}
            className="text-blue-600 hover:text-blue-700 h-8"
          >
            {language === 'fr' ? 'Plus tard' : 'Later'}
          </Button>
          <Button
            size="sm"
            onClick={handleEnable}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white h-8 rounded-full"
          >
            {loading ? '...' : (language === 'fr' ? 'Activer' : 'Enable')}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Offline Status Banner
export const OfflineBanner = () => {
  const isOnline = useOnlineStatus();
  const { language } = useLanguage();

  if (isOnline) return null;

  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 py-2">
      <div className="max-w-7xl mx-auto flex items-center justify-center gap-2">
        <WifiOff className="h-4 w-4 text-amber-600" />
        <p className="text-sm text-amber-800">
          {language === 'fr' 
            ? 'Vous êtes hors ligne. Certaines fonctionnalités peuvent être limitées.'
            : 'You are offline. Some features may be limited.'
          }
        </p>
      </div>
    </div>
  );
};

export default { InstallBanner, NotificationBanner, OfflineBanner };
